import type { Token } from './tokenizer';
import type { LogicNode } from '../types';

/**
 * Recursive descent parser for OoTR logic expressions.
 *
 * Grammar (precedence low→high):
 *   expr     = or_expr
 *   or_expr  = and_expr ('or' and_expr)*
 *   and_expr = not_expr ('and' not_expr)*
 *   not_expr = 'not' not_expr | primary
 *   primary  = 'True' | 'False'
 *            | STRING                           -> event
 *            | IDENT '==' (IDENT|STRING)        -> compare
 *            | IDENT '!=' (IDENT|STRING)        -> compare
 *            | STRING 'in' IDENT                -> contains
 *            | IDENT '(' args ')'               -> call
 *            | '(' IDENT ',' NUMBER ')'         -> has(item, count)
 *            | '(' expr ')'                     -> grouping
 *            | IDENT                             -> has(item, 1)
 */
export function parse(tokens: Token[]): LogicNode {
  let pos = 0;

  function peek(): Token {
    return tokens[pos] ?? { type: 'EOF', value: '' };
  }

  function advance(): Token {
    return tokens[pos++];
  }

  function expect(type: string): Token {
    const t = advance();
    if (t.type !== type) {
      throw new Error(`Expected ${type} but got ${t.type} ("${t.value}") at position ${pos - 1}`);
    }
    return t;
  }

  function parseExpr(): LogicNode {
    return parseOr();
  }

  function parseOr(): LogicNode {
    let left = parseAnd();
    while (peek().type === 'OR') {
      advance();
      const right = parseAnd();
      left = { type: 'or', left, right };
    }
    return left;
  }

  function parseAnd(): LogicNode {
    let left = parseNot();
    while (peek().type === 'AND') {
      advance();
      const right = parseNot();
      left = { type: 'and', left, right };
    }
    return left;
  }

  function parseNot(): LogicNode {
    if (peek().type === 'NOT') {
      advance();
      const child = parseNot();
      return { type: 'not', child };
    }
    return parsePrimary();
  }

  function parsePrimary(): LogicNode {
    const t = peek();

    // Literals
    if (t.type === 'TRUE') {
      advance();
      return { type: 'literal', value: true };
    }
    if (t.type === 'FALSE') {
      advance();
      return { type: 'literal', value: false };
    }

    // String literal -> event name or 'string' in identifier or compare
    if (t.type === 'STRING') {
      advance();
      // Check for 'string' in identifier  (e.g., 'Deku Tree' in dungeon_shortcuts)
      if (peek().type === 'IN') {
        advance();
        const ident = expect('IDENTIFIER');
        return { type: 'contains', left: t.value, right: ident.value };
      }
      return { type: 'event', name: t.value };
    }

    // Parenthesized expression or tuple (Item, Count)
    if (t.type === 'LPAREN') {
      advance();
      // Check if this is a tuple: (IDENTIFIER, NUMBER)
      if (peek().type === 'IDENTIFIER') {
        const savedPos = pos;
        const ident = advance();
        if (peek().type === 'COMMA') {
          advance();
          if (peek().type === 'NUMBER') {
            const num = advance();
            expect('RPAREN');
            return { type: 'has', item: ident.value, count: parseInt(num.value, 10) };
          }
        }
        // Not a tuple, backtrack and parse as grouped expression
        pos = savedPos;
      }
      const expr = parseExpr();
      expect('RPAREN');
      return expr;
    }

    // Identifier: could be function call, comparison, or simple item check
    if (t.type === 'IDENTIFIER') {
      advance();
      const name = t.value;

      // Function call: ident(args)
      if (peek().type === 'LPAREN') {
        advance();
        const args: string[] = [];
        if (peek().type !== 'RPAREN') {
          // Parse first argument
          args.push(parseArg());
          while (peek().type === 'COMMA') {
            advance();
            args.push(parseArg());
          }
        }
        expect('RPAREN');
        return { type: 'call', name, args };
      }

      // Comparison: ident == value  or  ident != value
      if (peek().type === 'EQ' || peek().type === 'NEQ') {
        const op = advance().value as '==' | '!=';
        let right: string;
        if (peek().type === 'STRING') {
          right = advance().value;
        } else if (peek().type === 'IDENTIFIER') {
          right = advance().value;
        } else if (peek().type === 'NUMBER') {
          right = advance().value;
        } else {
          right = advance().value;
        }
        return { type: 'compare', left: name, op, right };
      }

      // Simple item check
      return { type: 'has', item: name, count: 1 };
    }

    // Fallback
    advance();
    return { type: 'literal', value: true };
  }

  function parseArg(): string {
    // Arguments can be identifiers, strings, or even sub-expressions
    // For simplicity in function args, we capture the raw value
    const t = peek();
    if (t.type === 'STRING') {
      advance();
      return t.value;
    }
    if (t.type === 'IDENTIFIER') {
      advance();
      return t.value;
    }
    if (t.type === 'NUMBER') {
      advance();
      return t.value;
    }
    // For complex args (sub-expressions in at(), here()), collect tokens until matching paren
    // We'll parse them as a full expression and serialize back
    const expr = parseExpr();
    return serializeNode(expr);
  }

  const result = parseExpr();
  return result;
}

/** Serialize a LogicNode back to a string (for passing complex args to helpers) */
function serializeNode(node: LogicNode): string {
  switch (node.type) {
    case 'literal': return node.value ? 'True' : 'False';
    case 'and': return `(${serializeNode(node.left)} and ${serializeNode(node.right)})`;
    case 'or': return `(${serializeNode(node.left)} or ${serializeNode(node.right)})`;
    case 'not': return `not ${serializeNode(node.child)}`;
    case 'has': return node.count > 1 ? `(${node.item}, ${node.count})` : node.item;
    case 'call': return `${node.name}(${node.args.join(', ')})`;
    case 'compare': return `${node.left} ${node.op} '${node.right}'`;
    case 'event': return `'${node.name}'`;
    case 'contains': return `'${node.left}' in ${node.right}`;
  }
}
